import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import OnboardingSteps from '../components/OnboardingSteps';
import InstallPWA from '../components/InstallPWA';
import { 
  ChevronRight, 
  Star, 
  Heart, 
  Brain, 
  Footprints,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  Menu,
  X,
  Play,
  Shield,
  Zap,
  Users,
  Award,
  Camera,
  Scan,
  Crown,
  MessageCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const HERO_IMAGE = "https://images.pexels.com/photos/9483564/pexels-photo-9483564.jpeg?auto=compress&cs=tinysrgb&w=1920";

// Plans data
const PLANS = [
  {
    id: 'pack5',
    name: 'Essencial',
    price: 19.90,
    yearlyPrice: 199.00,
    readings: 5,
    popular: false,
    features: [
      '5 leituras mensais',
      'Mapa reflexológico básico',
      'Recomendações gerais',
      'Histórico de leituras'
    ]
  },
  {
    id: 'detailed10',
    name: 'Plus',
    price: 49.90,
    yearlyPrice: 499.00,
    readings: 10,
    popular: true,
    features: [
      '10 leituras mensais',
      'Análise detalhada por zonas',
      'Score de reatividade',
      'Protocolo personalizado',
      'Suporte por email'
    ]
  },
  {
    id: 'advanced',
    name: 'Premium',
    price: 99.00,
    yearlyPrice: 990.00,
    readings: 20,
    popular: false,
    features: [
      '20 leituras mensais',
      'Análise completa',
      'Histórico evolutivo',
      'Recomendações avançadas',
      'Relatório visual compartilhável',
      'Suporte prioritário'
    ]
  }
];

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isYearly, setIsYearly] = useState(false);
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  const handleStartReading = () => {
    if (user) {
      navigate('/nova-leitura');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Install PWA Prompt */}
      <InstallPWA />
      
      {/* Header */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="px-4 pt-3">
          <div className="max-w-6xl mx-auto rounded-full px-5 py-2.5 flex items-center justify-between"
               style={{
                 background: 'rgba(10, 15, 20, 0.85)',
                 backdropFilter: 'blur(24px)',
                 WebkitBackdropFilter: 'blur(24px)',
                 border: '1px solid rgba(34, 211, 238, 0.18)',
                 boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 30px rgba(34,211,238,0.08)',
               }}>
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
              <img src={LOGO_URL} alt="AJ HealthMap" className="h-12 md:h-14 w-auto drop-shadow-lg" />
            </Link>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7">
              <a href="#como-funciona" className="text-white/70 hover:text-cyan-300 transition-colors font-semibold text-sm tracking-wide">
                Como funciona
              </a>
              <a href="#depoimentos" className="text-white/70 hover:text-cyan-300 transition-colors font-semibold text-sm tracking-wide">
                Depoimentos
              </a>
              <a href="#planos" className="text-white/70 hover:text-cyan-300 transition-colors font-semibold text-sm tracking-wide">
                Planos
              </a>
              <a href="#seguranca" className="text-white/70 hover:text-cyan-300 transition-colors font-semibold text-sm tracking-wide">
                Segurança
              </a>
            </nav>
            
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="text-white rounded-full px-5 h-9 font-bold text-sm"
                  style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 4px 18px rgba(212,162,86,0.4)' }}
                  data-testid="header-dashboard-btn"
                >
                  Meu Painel
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="text-cyan-100 hover:text-white hover:bg-white/5 rounded-full h-9"
                    data-testid="header-login-btn"
                  >
                    Entrar
                  </Button>
                  <Button 
                    onClick={() => navigate('/login')}
                    className="text-white rounded-full px-5 h-9 font-bold text-sm"
                    style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 4px 18px rgba(212,162,86,0.4)' }}
                    data-testid="header-cta-btn"
                  >
                    Começar Grátis
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mx-4 mt-2 rounded-2xl overflow-hidden"
              style={{
                background: 'rgba(10, 15, 20, 0.95)',
                backdropFilter: 'blur(24px)',
                border: '1px solid rgba(34, 211, 238, 0.18)',
              }}
            >
              <nav className="flex flex-col p-4 gap-3">
                <a href="#como-funciona" onClick={() => setIsMenuOpen(false)} className="text-white/80 py-2 font-semibold">Como funciona</a>
                <a href="#depoimentos" onClick={() => setIsMenuOpen(false)} className="text-white/80 py-2 font-semibold">Depoimentos</a>
                <a href="#planos" onClick={() => setIsMenuOpen(false)} className="text-white/80 py-2 font-semibold">Planos</a>
                <a href="#seguranca" onClick={() => setIsMenuOpen(false)} className="text-white/80 py-2 font-semibold">Segurança</a>
                <hr className="border-white/10" />
                {user ? (
                  <Button onClick={() => navigate('/dashboard')} className="w-full rounded-full text-white font-bold"
                          style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)' }}>
                    Meu Painel
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/login')} className="w-full rounded-full text-white font-bold"
                          style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)' }}>
                    Começar Grátis
                  </Button>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top, #0F766E 0%, #083D3A 50%, #0A0F14 100%)' }} />
          {/* Scanner grid */}
          <div className="absolute inset-0 scanner-grid opacity-25" />
          {/* Neon orbs */}
          <div className="absolute top-20 right-10 w-96 h-96 rounded-full"
               style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.25) 0%, transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-20 -left-20 w-[28rem] h-[28rem] rounded-full"
               style={{ background: 'radial-gradient(circle, rgba(212,162,86,0.22) 0%, transparent 70%)', filter: 'blur(40px)' }} />
          {/* Animated scan line */}
          <motion.div
            className="absolute left-0 right-0 h-[2px] z-10"
            style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', opacity: 0.6 }}
            animate={{ top: ['10%', '90%', '10%'] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center md:text-left"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6"
                style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.3)' }}
              >
                <Scan className="w-4 h-4 text-cyan-300" />
                <span className="text-cyan-100 text-sm font-semibold tracking-wide uppercase">Scanner de IA Reflexológica</span>
              </motion.div>

              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Seu mapa reflexológico{' '}
                <span style={{ background: 'linear-gradient(135deg, #22D3EE 0%, #D4A256 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>inteligente</span>
              </motion.h1>

              <motion.p 
                className="text-lg md:text-xl text-white/80 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Escaneie a sola do pé com IA avançada e desbloqueie um mapa personalizado em segundos.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button 
                  onClick={handleStartReading}
                  className="h-14 px-8 rounded-full text-white text-lg font-bold transition-all hover:shadow-2xl hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #D4A256 0%, #E8BC6A 100%)', boxShadow: '0 8px 30px rgba(212,162,86,0.5)' }}
                  data-testid="hero-start-btn"
                >
                  <Scan className="w-5 h-5 mr-2" />
                  Iniciar scanner grátis
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
                  className="h-14 px-8 rounded-full text-lg font-semibold"
                  style={{ border: '2px solid rgba(34,211,238,0.5)', color: '#22D3EE', background: 'rgba(34,211,238,0.05)' }}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Ver como funciona
                </Button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div 
                className="flex items-center justify-center md:justify-start gap-6 mt-10 pt-8 border-t border-white/10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">10k+</div>
                  <div className="text-xs text-white/60">Leituras</div>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">4.9</div>
                  <div className="text-xs text-white/60 flex items-center gap-1">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Avaliação
                  </div>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">100%</div>
                  <div className="text-xs text-white/60">Seguro</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image/Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden md:block"
            >
              <div className="relative">
                {/* Phone Mockup */}
                <div className="relative bg-[#1A1A1A] rounded-[3rem] p-3 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-[#FDFBF7] rounded-[2.5rem] overflow-hidden">
                    <img 
                      src={HERO_IMAGE} 
                      alt="Leitura reflexológica" 
                      className="w-full h-[500px] object-cover"
                    />
                    {/* Scanning Overlay */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4A373] to-transparent"
                        animate={{ top: ['10%', '90%', '10%'] }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                      />
                    </motion.div>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <motion.div
                  className="absolute -left-8 top-20 bg-white rounded-2xl p-4 shadow-xl"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-[#8E8E93]">Score</div>
                      <div className="font-bold text-[#1C1C1E]">87/100</div>
                    </div>
                  </div>
                </motion.div>
                
                <motion.div
                  className="absolute -right-4 bottom-32 bg-white rounded-2xl p-4 shadow-xl"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4A373]/10 rounded-xl flex items-center justify-center">
                      <Brain className="w-5 h-5 text-[#D4A373]" />
                    </div>
                    <div>
                      <div className="text-xs text-[#8E8E93]">Zonas</div>
                      <div className="font-bold text-[#1C1C1E]">12 detectadas</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <motion.div 
              className="w-1.5 h-1.5 bg-white rounded-full"
              animate={{ y: [0, 16, 0], opacity: [1, 0, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section id="como-funciona" className="py-20 md:py-32 relative overflow-hidden"
               style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
        <div className="absolute inset-0 scanner-grid opacity-[0.06] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="hud-chip mb-4">
              <Scan className="w-3.5 h-3.5" />
              Processo de Análise
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#083D3A] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Simples, rápido e{' '}
              <span style={{ background: 'linear-gradient(135deg, #0F766E, #22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                inteligente
              </span>
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto text-lg">
              4 passos para sua leitura reflexológica completa
            </p>
          </motion.div>

          <OnboardingSteps />
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="depoimentos" className="py-20 md:py-28 relative overflow-hidden"
               style={{ background: 'radial-gradient(ellipse at top, #0F766E 0%, #083D3A 60%, #0A0F14 100%)' }}>
        <div className="absolute inset-0 scanner-grid opacity-15 pointer-events-none" />
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(212,162,86,0.2) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="hud-chip hud-chip-gold mb-4">
              <Star className="w-3.5 h-3.5 fill-current" />
              Avaliações Verificadas
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Histórias de quem{' '}
              <span style={{ background: 'linear-gradient(135deg, #22D3EE, #D4A256)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                se cuida com IA
              </span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto text-lg">
              Mais de 5.000 leituras realizadas em todo o Brasil
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Mariana S.',
                role: 'Terapeuta Holística',
                location: 'São Paulo, SP',
                text: 'Uso o AJ HealthMap como apoio nas sessões. A precisão das zonas é impressionante e meus clientes adoram o relatório visual.',
                accent: '#22D3EE',
              },
              {
                name: 'Carlos R.',
                role: 'Empresário',
                location: 'Belo Horizonte, MG',
                text: 'Achei que seria mais um app. Mas o nível de detalhamento das recomendações realmente me ajudou a entender meus pontos de tensão.',
                accent: '#D4A256',
              },
              {
                name: 'Beatriz L.',
                role: 'Praticante de Reflexologia',
                location: 'Florianópolis, SC',
                text: 'A combinação de mapa visual + protocolo passo a passo é genial. Compartilho leituras com minha rede de bem-estar.',
                accent: '#22D3EE',
              },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                whileHover={{ y: -6 }}
                className="rounded-3xl p-6 relative overflow-hidden"
                style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${t.accent}33`,
                  boxShadow: `0 10px 30px rgba(0,0,0,0.3), 0 0 30px ${t.accent}15`,
                }}
              >
                {/* Quote mark */}
                <div className="absolute top-3 right-4 text-6xl font-bold opacity-10"
                     style={{ color: t.accent, fontFamily: 'Syne, sans-serif' }}>
                  "
                </div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                
                <p className="text-white/85 text-sm leading-relaxed mb-6 relative z-10">
                  "{t.text}"
                </p>
                
                <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white"
                       style={{ background: `linear-gradient(135deg, ${t.accent}, ${t.accent}99)` }}>
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm" style={{ fontFamily: 'Syne, sans-serif' }}>{t.name}</p>
                    <p className="text-white/50 text-xs">{t.role} · {t.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="planos" className="py-20 md:py-32 relative overflow-hidden"
               style={{ background: 'linear-gradient(180deg, #F8F5EF 0%, #FDF9F3 100%)' }}>
        <div className="absolute inset-0 scanner-grid opacity-[0.05] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="hud-chip hud-chip-gold mb-4">
              <Crown className="w-3.5 h-3.5" />
              Planos & Upgrade
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#083D3A] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Escolha o plano{' '}
              <span style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                ideal para você
              </span>
            </h2>
            <p className="text-[#6B7280] max-w-2xl mx-auto mb-8 text-lg">
              Comece com leitura gratuita e desbloqueie o protocolo premium quando quiser
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={`text-sm font-bold ${!isYearly ? 'text-[#083D3A]' : 'text-[#9CA3AF]'}`}>Mensal</span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className="relative w-14 h-7 rounded-full transition-colors"
                style={{ background: isYearly ? 'linear-gradient(135deg, #0F766E, #22D3EE)' : '#D1D5DB' }}
                data-testid="billing-toggle"
              >
                <motion.div
                  className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-lg"
                  animate={{ left: isYearly ? '32px' : '4px' }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
              <span className={`text-sm font-bold ${isYearly ? 'text-[#083D3A]' : 'text-[#9CA3AF]'}`}>
                Anual <span className="text-emerald-600 text-xs ml-1">(-2 meses)</span>
              </span>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className={`relative rounded-3xl p-6 transition-all duration-300 ${
                  plan.popular ? 'md:scale-105 z-10' : ''
                }`}
                style={
                  plan.popular
                    ? {
                        background: 'radial-gradient(ellipse at top, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
                        boxShadow: '0 25px 60px rgba(8,61,58,0.5), 0 0 40px rgba(212,162,86,0.25)',
                        border: '1px solid rgba(212,162,86,0.4)',
                      }
                    : {
                        background: 'white',
                        border: '1px solid rgba(15,118,110,0.15)',
                        boxShadow: '0 10px 30px rgba(8,61,58,0.08)',
                      }
                }
              >
                {plan.popular && (
                  <motion.div 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase text-[#0A0F14]"
                    style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 6px 20px rgba(212,162,86,0.5)' }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    Mais Popular
                  </motion.div>
                )}

                <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-[#083D3A]'}`} style={{ fontFamily: 'Syne, sans-serif' }}>
                  {plan.name}
                </h3>
                
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-[#083D3A]'}`}>
                    R${(isYearly ? plan.yearlyPrice / 12 : plan.price).toFixed(2).replace('.', ',')}
                  </span>
                  <span className={`text-sm ${plan.popular ? 'text-white/60' : 'text-[#9CA3AF]'}`}>/mês</span>
                  {isYearly && (
                    <div className={`text-xs mt-1 ${plan.popular ? 'text-amber-300' : 'text-[#9CA3AF]'}`}>
                      Cobrado R${plan.yearlyPrice.toFixed(2).replace('.', ',')} anualmente
                    </div>
                  )}
                </div>

                <div className={`text-sm mb-6 font-bold tracking-wide uppercase ${plan.popular ? 'text-cyan-300' : 'text-[#0F766E]'}`}>
                  {plan.readings} leituras/mês
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                           style={{ background: plan.popular ? 'rgba(212,162,86,0.2)' : 'rgba(15,118,110,0.1)' }}>
                        <CheckCircle2 className={`w-3.5 h-3.5 ${plan.popular ? 'text-amber-300' : 'text-[#0F766E]'}`} />
                      </div>
                      <span className={`text-sm ${plan.popular ? 'text-white/85' : 'text-[#374151]'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => navigate('/login')}
                  className="w-full h-12 rounded-full font-bold transition-all"
                  style={
                    plan.popular
                      ? { background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', color: '#0A0F14', boxShadow: '0 6px 20px rgba(212,162,86,0.5)' }
                      : { background: 'linear-gradient(135deg, #083D3A, #0F766E)', color: 'white' }
                  }
                  data-testid={`landing-plan-${plan.id}-btn`}
                >
                  {plan.popular ? 'Começar agora' : 'Escolher plano'}
                </Button>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-[#6B7280] text-sm mt-8 flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4 text-amber-500" />
            Primeira leitura gratuita para novos usuários
          </motion.p>
        </div>
      </section>

      {/* Security & LGPD Section */}
      <section id="seguranca" className="py-20 md:py-28 relative overflow-hidden"
               style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="hud-chip mb-4">
                <Shield className="w-3.5 h-3.5" />
                Privacidade & LGPD
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#083D3A] mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                Seus dados são seus.{' '}
                <span style={{ background: 'linear-gradient(135deg, #0F766E, #22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Sempre.
                </span>
              </h2>
              <p className="text-[#4B5563] mb-6 leading-relaxed">
                Tratamos cada imagem e leitura com nível bancário de segurança. Você controla, exporta ou apaga seus dados quando quiser — em conformidade total com a LGPD.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: Shield, text: 'Criptografia em trânsito e em repouso' },
                  { icon: Zap, text: 'Análise em servidores brasileiros' },
                  { icon: CheckCircle2, text: 'Botão "Excluir conta" em 1 clique' },
                  { icon: Sparkles, text: 'Imagens nunca compartilhadas com terceiros' },
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                         style={{ background: 'rgba(15,118,110,0.1)', border: '1px solid rgba(15,118,110,0.2)' }}>
                      <item.icon className="w-4 h-4 text-[#0F766E]" />
                    </div>
                    <span className="text-[#374151] font-medium">{item.text}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-3xl p-8 overflow-hidden aspect-square max-w-md mx-auto"
                   style={{
                     background: 'radial-gradient(ellipse at center, #0F766E 0%, #083D3A 70%, #0A0F14 100%)',
                     boxShadow: '0 25px 60px rgba(8,61,58,0.4)',
                     border: '1px solid rgba(34,211,238,0.25)',
                   }}>
                <div className="absolute inset-0 scanner-grid opacity-30 pointer-events-none" />
                <motion.div
                  className="absolute left-0 right-0 h-[2px]"
                  style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', boxShadow: '0 0 15px #22D3EE' }}
                  animate={{ top: ['10%', '90%', '10%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
                  <motion.div
                    className="w-28 h-28 rounded-full flex items-center justify-center mb-6"
                    style={{
                      background: 'rgba(34,211,238,0.12)',
                      border: '1px solid rgba(34,211,238,0.4)',
                      boxShadow: '0 0 40px rgba(34,211,238,0.4)',
                    }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Shield className="w-14 h-14 text-cyan-300" />
                  </motion.div>
                  <p className="text-cyan-300 text-xs font-bold tracking-[0.3em] uppercase mb-3">Protocolo</p>
                  <h3 className="text-white text-3xl font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                    AES-256
                  </h3>
                  <p className="text-white/60 text-sm max-w-xs">
                    Padrão de criptografia militar para suas leituras e imagens.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden"
               style={{ background: 'radial-gradient(ellipse at center, #0F766E 0%, #083D3A 60%, #0A0F14 100%)' }}>
        <div className="absolute inset-0 scanner-grid opacity-20 pointer-events-none" />
        <motion.div
          className="absolute left-0 right-0 h-[2px]"
          style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, transparent)', opacity: 0.5 }}
          animate={{ top: ['10%', '90%', '10%'] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="hud-chip mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Pronto para começar?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
              Descubra o que seus pés{' '}
              <span style={{ background: 'linear-gradient(135deg, #22D3EE, #D4A256)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                revelam sobre você
              </span>
            </h2>
            <p className="text-white/70 mb-10 text-lg max-w-2xl mx-auto">
              Sua primeira leitura é grátis. Sem cartão de crédito.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleStartReading}
                className="h-14 px-10 rounded-full text-[#0A0F14] text-lg font-bold transition-all hover:shadow-2xl hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 12px 40px rgba(212,162,86,0.5)' }}
                data-testid="cta-start-btn"
              >
                <Scan className="w-5 h-5 mr-2" />
                Iniciar scanner grátis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            <p className="text-white/40 text-xs mt-6 tracking-wider uppercase">Sem compromisso · Cancele quando quiser</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="sobre" className="text-white py-16 relative overflow-hidden"
              style={{ background: '#0A0F14' }}>
        <div className="absolute inset-0 scanner-grid opacity-[0.08] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="AJ HealthMap" className="h-20 mb-4 drop-shadow-2xl" />
              <p className="text-white/60 text-sm mb-5 max-w-md leading-relaxed">
                AJ HealthMap usa inteligência artificial para entregar leituras reflexológicas educacionais com precisão de scanner profissional. Bem-estar guiado por tecnologia.
              </p>
              <div className="flex items-center gap-4 text-white/60 flex-wrap">
                <a href="mailto:contato@ajhealthmap.com.br" className="flex items-center gap-2 hover:text-amber-300 transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">contato@ajhealthmap.com.br</span>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-cyan-300 tracking-wider text-xs uppercase">Navegação</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><a href="#como-funciona" className="hover:text-cyan-300 transition-colors">Como funciona</a></li>
                <li><a href="#depoimentos" className="hover:text-cyan-300 transition-colors">Depoimentos</a></li>
                <li><a href="#planos" className="hover:text-cyan-300 transition-colors">Planos</a></li>
                <li><a href="#seguranca" className="hover:text-cyan-300 transition-colors">Segurança</a></li>
                <li><Link to="/login" className="hover:text-cyan-300 transition-colors">Entrar</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-amber-300 tracking-wider text-xs uppercase">Legal</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li><Link to="/termos" className="hover:text-amber-300 transition-colors">Termos de Uso</Link></li>
                <li><Link to="/privacidade" className="hover:text-amber-300 transition-colors">Privacidade</Link></li>
                <li><Link to="/cookies" className="hover:text-amber-300 transition-colors">Cookies</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="border-t border-white/10 pt-8 mb-8">
            <div className="rounded-2xl p-5 text-white/60 text-xs leading-relaxed"
                 style={{ background: 'rgba(212,162,86,0.05)', border: '1px solid rgba(212,162,86,0.15)' }}>
              <strong className="text-amber-300">Aviso importante:</strong> Este conteúdo é educacional e voltado ao bem-estar. 
              A leitura reflexológica não constitui diagnóstico médico, não substitui consulta com profissional de saúde 
              e não deve ser usada para decisões clínicas. Imagens enviadas são usadas apenas para gerar a leitura e 
              podem ser excluídas pelo usuário a qualquer momento na área de conta.
            </div>
          </div>
          
          <div className="text-center text-white/40 text-sm flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            © {new Date().getFullYear()} AJ HealthMap. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
